import { v4 as uuidv4 } from "uuid";
import { ListTodo, PlusIcon, Trash2Icon } from "lucide-react";
import { useEffect, useState } from "react";
import * as Switch from "@radix-ui/react-switch";

import { cn } from "../../../lib/utils";
import { PARTY_TYPE_OPTIONS, PartyInvolved } from "../../../api/PartiesInvolved";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import FormElement from "../../../components/ui/formelement/FromElement";
import SelectDropdownV2 from "../../../components/ui/selectdropdown/v2";
import { Input } from "../../../components/ui/input/SimpleInput";
import ButtonV2 from "../../../components/ui/button/v2";
import { AccountType, AREA_OF_FRAUD_LABEL_MAP, AreaOfFraud } from "../../../api/cases";
import { useAppSelector } from "../../../app/store";
import { getProducts } from "../../configs/logic/Slice";
import { LoanProductInfoRow, PartyInvolvedState, validateField } from "../utils/helper";
import { RenderSelectedPills } from "../utils/common";
import StepNavigation from "./StepNavigation";

/**
 * Legacy Party-Involved UI for Operational / Cyber cases: Area of Fraud +
 * Account Type + loan-product rows + employee-involvement toggle + party rows.
 * (Credit uses the new V5 section design.)
 */
type PartyInvolvedStepLegacyProps = {
  value: PartyInvolvedState;
  handleFormInputChange: (fieldName: string, value: any) => void;
  onNext: () => void;
  onPrevious: () => void;
};

const AREA_OF_FRAUD_OPTIONS: OptionNameValuePair<string>[] = [
  { name: AREA_OF_FRAUD_LABEL_MAP[AreaOfFraud.CREDIT], value: AreaOfFraud.CREDIT },
  { name: AREA_OF_FRAUD_LABEL_MAP[AreaOfFraud.CYBER], value: AreaOfFraud.CYBER },
  { name: AREA_OF_FRAUD_LABEL_MAP[AreaOfFraud.OPERATIONAL], value: AreaOfFraud.OPERATIONAL },
];

const ACCOUNT_TYPE_OPTIONS: OptionNameValuePair<string>[] = [
  { name: "Term Loan", value: AccountType.TERM_LOAN },
  { name: "Cash Credit (WC)", value: AccountType.CASH_CREDIT_WC },
  { name: "Bank Guarantee", value: AccountType.BANK_GUARANTEE },
  { name: "Letter of Credit", value: AccountType.LETTER_OF_CREDIT },
  { name: "Others", value: AccountType.OTHERS },
];

export const validatePartyInvolved = (party: PartyInvolved): boolean | string => {
    if (!party.party_type || party.party_type.trim() === "") return "Party type is required.";
    if (!party.name || party.name.trim() === "") return "Party name is required.";
    return true;
};

export const validateLoanRow = (row: LoanProductInfoRow): true | string => {
    if (!row.product) return "Product is required.";
    if (!row.loanReferenceIds || row.loanReferenceIds.length === 0) return "At least one Account Number is required.";
    return true;
};

const PartyInvolvedStepLegacy: React.FC<PartyInvolvedStepLegacyProps> = ({
  value,
  handleFormInputChange,
  onNext,
  onPrevious,
}) => {
  const [employeeId, setEmployeeId] = useState<string>("");
  const [partyLevelError, setPartyLevelError] = useState<string>("");
  const [loanLevelError, setLoanLevelError] = useState<string>("");
  const [loanInputMap, setLoanInputMap] = useState<Record<string, string>>({});

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const products = useAppSelector(getProducts);
  const productOptions: OptionNameValuePair<string>[] = products.map((product) => ({ name: product, value: product }));

  const areaOfFraud = value.area_of_fraud;
  const accountType = value.account_type;
  const hasEmployeeInvolvement = value.has_employee_involvement  ?? false;
  const employeeIds = value.employee_ids;
  const partiesInvolved = value.parties_involved ?? [];
  const loanDetails = value.loanDetails;

  const isOperational = areaOfFraud?.value === AreaOfFraud.OPERATIONAL;

  useEffect(() => {
    if (isOperational && !hasEmployeeInvolvement) handleFormInputChange("has_employee_involvement", true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOperational]);

  useEffect(() => {
    if (!hasEmployeeInvolvement && employeeIds && employeeIds.length > 0) handleFormInputChange("employee_ids", []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEmployeeInvolvement]);

  const handleEmployeeRemoval = (index: number) => {
    if (!employeeIds) return;
    handleFormInputChange("employee_ids", employeeIds.filter((_, i) => i !== index));
  };

  const handlePartyInvolveAdd = () => {
    const currentIndex = partiesInvolved.length;
    if (currentIndex > 0) {
      const valid = validatePartyInvolved(partiesInvolved[currentIndex - 1]);
      if (valid !== true) {
        setPartyLevelError(typeof valid === "string" ? valid : "Please fill the current party involved details before adding a new one.");
        return;
      }
    }
    setPartyLevelError("");
    handleFormInputChange("parties_involved", [...partiesInvolved, { party_id: uuidv4(), party_type: "", name: "" }]);
  };

  const handleLoanRowAdd = () => {
    if (loanDetails && loanDetails.length > 0) {
      for (let i = 0; i < loanDetails.length; i++) {
        const result = validateLoanRow(loanDetails[i]);
        if (result !== true) { setLoanLevelError(` ${result}`); return; }
      }
    }
    setLoanLevelError("");
    handleFormInputChange("loanDetails", [...(loanDetails || []), { id: uuidv4(), product: "", loanReferenceIds: [] }]);
  };

  const handleLoanRowDelete = (index: number) => {
    if (!loanDetails) return;
    handleFormInputChange("loanDetails", loanDetails.filter((_, i) => i !== index));
  };

  const handlePartyInvolveDelete = (index: number) => {
    handleFormInputChange("parties_involved", partiesInvolved.filter((_, i) => i !== index));
  };

  const handlePartyInvolveChange = (index: number, field: string, value: string) => {
    const updated = [...partiesInvolved];
    updated[index] = { ...updated[index], [field]: value };
    handleFormInputChange("parties_involved", updated);
  };

  const handleLoanProductChange = (index: number, value: OptionNameValuePair<string>) => {
    if (!loanDetails) return;
    const updated = [...loanDetails];
    updated[index] = { ...updated[index], product: value.value };
    handleFormInputChange("loanDetails", updated);
    setLoanLevelError("");
  };

  const handleLoanReferenceEnter = (e: React.KeyboardEvent<HTMLInputElement>, index: number, rowId: string) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const value = loanInputMap[rowId]?.trim();
    if (!value || !loanDetails) return;
    const updated = [...loanDetails];
    updated[index].loanReferenceIds = Array.from(new Set([...updated[index].loanReferenceIds, value]));
    handleFormInputChange("loanDetails", updated);
    setLoanLevelError("");
    setLoanInputMap((prev) => ({ ...prev, [rowId]: "" }));
  };

  const validatePartyInvolvedStep = (v: PartyInvolvedState): Record<string, string> => {
      const errors: Record<string, string> = {};
      (["area_of_fraud", "account_type"] as const).forEach((field) => {
          const error = validateField(field, (v as any)[field]);
          if (error) errors[field] = error;
      });
  
      const isOperational = v.area_of_fraud?.value === AreaOfFraud.OPERATIONAL;
      const isAccountNumbersRequired = !isOperational && v.account_type?.value !== AccountType.OTHERS;
  
      if (isAccountNumbersRequired && (!v.loanDetails || v.loanDetails.length === 0)) {
          errors["loanDetails"] = "At least one Account Number entry is required.";
      } else if (v.loanDetails && v.loanDetails.length > 0) {
          v.loanDetails.forEach((row) => {
              const result = validateLoanRow(row);
              if (result !== true) errors["loanDetails"] = ` ${result}`;
          });
      }
  
      if (v.has_employee_involvement || isOperational) {
          if (!v.employee_ids || v.employee_ids.length === 0) {
              errors["employee_ids"] = "At least one Employee ID is required when there is employee involvement.";
          }
      }
  
      v.parties_involved?.forEach((party) => {
          const partyValidationResult = validatePartyInvolved(party);

          if (partyValidationResult !== true) {
              errors["parties_involved"] = partyValidationResult as string;
          }
      });
      return errors;
  };

  const handleLegacyNext = () => {
    const errors = validatePartyInvolvedStep(value);
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) onNext();
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 gap-y-2">
        <FormElement
          name="Area of Fraud"
          className="mb-4"
          inputComponent={
            <SelectDropdownV2
              selectedOption={areaOfFraud}
              name="type"
              enableSearch={false}
              options={AREA_OF_FRAUD_OPTIONS}
              placeholder="Select type"
              onSelectionChange={handleFormInputChange.bind(null, "area_of_fraud")}
              containerClassName="border border-gray-300 rounded-md py-2 px-3"
              labelClassName="font-medium"
            />
          }
          error={formErrors["area_of_fraud"]}
        />

        {areaOfFraud && (
          <FormElement
            name="Account Type"
            className="mb-4"
            inputComponent={
              <SelectDropdownV2
                selectedOption={accountType}
                name="account type"
                enableSearch={false}
                options={ACCOUNT_TYPE_OPTIONS}
                placeholder="Select account type"
                onSelectionChange={handleFormInputChange.bind(null, "account_type")}
                containerClassName="border border-gray-300 rounded-md py-2 px-3"
                labelClassName="font-medium"
              />
            }
            error={formErrors["account_type"]}
          />
        )}

        {areaOfFraud && accountType && (
          <>
            <div className="md:col-span-2">
              <div className="flex flex-col gap-4 bg-[#EBEEFE] rounded-md mb-4 p-3">
                <FormElement
                  name="Account Numbers"
                  className="mb-0"
                  isMandatory={!isOperational}
                  headerRightChildren={
                    <ButtonV2 variant="ghost" size="sm" onClick={handleLoanRowAdd} className="text-[#3551F3] bg-transparent" prefix={<PlusIcon width="16" height="16" />}>
                      Add
                    </ButtonV2>
                  }
                  inputComponent={
                    <div className={cn("flex flex-col gap-4 mt-4", (loanDetails?.length ?? 0) === 0 && "mt-0")}>
                      {(loanDetails || []).map((row, index) => (
                        <div key={row.id} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium">Product <span className="text-red-500">*</span></label>
                            <SelectDropdownV2
                              name="Product"
                              selectedOption={row.product ? { value: row.product, name: row.product } : undefined}
                              enableSearch={true}
                              options={productOptions}
                              containerClassName="border border-gray-300 rounded-md bg-white h-9"
                              labelClassName="py-2 px-3"
                              onSelectionChange={(opt) => handleLoanProductChange(index, opt)}
                            />
                          </div>
                          <div className="flex grow gap-4">
                            <div className="flex flex-col gap-1 grow">
                              <label className="text-xs font-medium">Account Numbers <span className="text-red-500">*</span></label>
                              <Input
                                value={loanInputMap[row.id] || ""}
                                placeholder="Account Number"
                                className="w-full border border-gray-300 rounded-md h-9 px-3 py-0"
                                onChange={(e) => setLoanInputMap((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                allowEnterAdd={true}
                                onKeyDown={(e) => handleLoanReferenceEnter(e, index, row.id)}
                              />
                              {row.loanReferenceIds.length > 0 && (
                                <RenderSelectedPills
                                  data={row.loanReferenceIds}
                                  onRemove={(pillIndex) => {
                                    if (!loanDetails) return;
                                    const updated = [...loanDetails];
                                    updated[index] = { ...updated[index], loanReferenceIds: updated[index].loanReferenceIds.filter((_, i) => i !== pillIndex) };
                                    handleFormInputChange("loanDetails", updated);
                                  }}
                                />
                              )}
                            </div>
                            <div className="flex items-start pt-[20px]">
                              <ButtonV2 variant="outline" size="sm" onClick={() => handleLoanRowDelete(index)} className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500" prefix={<Trash2Icon width="16" height="16" />} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  }
                  error={formErrors["loanDetails"] || loanLevelError}
                />
              </div>
            </div>

            <div className="md:col-span-2 flex items-center gap-4 mb-4">
              <Switch.Root
                className={`w-12 h-6 rounded-full relative shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${hasEmployeeInvolvement ? "bg-[#3551F3]" : "bg-gray-200"} ${isOperational ? "opacity-70 cursor-not-allowed" : ""}`}
                checked={hasEmployeeInvolvement}
                disabled={isOperational}
                onCheckedChange={(checked) => handleFormInputChange("has_employee_involvement", checked)}
              >
                <Switch.Thumb className={`block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${hasEmployeeInvolvement ? "translate-x-[26px]" : "translate-x-[2px]"}`} />
              </Switch.Root>
              <span className="text-sm font-medium text-gray-700">Employee Involvement</span>
            </div>

            <div className="md:col-span-2">
              <div className="flex flex-col gap-4 mb-4">
                <FormElement
                  name="Employee ID"
                  className="mb-0"
                  isMandatory={isOperational || hasEmployeeInvolvement}
                  inputComponent={
                    <Input
                      type="text"
                      value={employeeId}
                      disabled={!hasEmployeeInvolvement}
                      placeholder="Enter Employee ID"
                      className="py-2 px-3 disabled:cursor-not-allowed disabled:bg-gray-100"
                      allowEnterAdd={true}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const value = employeeId.trim();
                          if (value === "") return;
                          const uniqEmpIds = new Set([...(employeeIds || []), employeeId]);
                          handleFormInputChange("employee_ids", Array.from(uniqEmpIds));
                          setEmployeeId("");
                        }
                      }}
                    />
                  }
                  error={formErrors["employee_ids"]}
                />
                {employeeIds && employeeIds.length > 0 && (
                  <RenderSelectedPills data={employeeIds} onRemove={handleEmployeeRemoval} />
                )}
              </div>
            </div>

            <div className="md:col-span-2">
              <div className="flex flex-col gap-4 bg-[#EBEEFE] rounded-md mb-4 p-3">
                <FormElement
                  name="Primary Parties"
                  isMandatory={false}
                  className="mb-0"
                  inputComponent={
                    <div className={cn("flex flex-col gap-4 mt-4", partiesInvolved.length === 0 && "mt-0")}>
                      {partiesInvolved.map((partyInvolved, index) => {
                        const selectedPartyTypeOption: OptionNameValuePair<string> | undefined = partyInvolved.party_type
                          ? { name: partyInvolved.party_type.replace("_", " "), value: partyInvolved.party_type }
                          : undefined;
                        return (
                          <div key={index.toString()} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="flex flex-col gap-1">
                              <label className="text-xs font-medium">Party Type <span className="text-red-500">*</span></label>
                              <SelectDropdownV2
                                name="party type"
                                selectedOption={selectedPartyTypeOption}
                                enableSearch={false}
                                containerClassName="border border-gray-300 rounded-md bg-white"
                                labelClassName="py-2 px-3"
                                options={Object.entries(PARTY_TYPE_OPTIONS).map(([, val]) => ({ name: val.replace("_", " "), value: val }))}
                                onSelectionChange={(opt) => handlePartyInvolveChange(index, "party_type", opt.value)}
                              />
                            </div>
                            <div className="flex grow gap-4">
                              <div className="flex flex-col gap-1 grow">
                                <label className="text-xs font-medium">Party Name <span className="text-red-500">*</span></label>
                                <Input
                                  placeholder="Enter party name"
                                  className="w-full border border-gray-300 rounded-md px-3 py-[9px] focus:outline-none focus:ring-2 focus:ring-blue-500 leading-[1.3]"
                                  type="text"
                                  value={partyInvolved.name || ""}
                                  onChange={(event) => handlePartyInvolveChange(index, "name", event.target.value)}
                                />
                              </div>
                              <div className="flex items-end">
                                <ButtonV2 variant="outline" size="sm" onClick={() => handlePartyInvolveDelete(index)} className="px-2 rounded-md bg-white py-[18px] border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500" prefix={<Trash2Icon width="16" height="16" />} />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  }
                  headerRightChildren={
                    <ButtonV2 variant="ghost" size="sm" onClick={handlePartyInvolveAdd} className="text-[#3551F3] bg-transparent" prefix={<PlusIcon width="16" height="16" />}>
                      Add
                    </ButtonV2>
                  }
                  error={formErrors["parties_involved"] || partyLevelError}
                />
              </div>
            </div>
          </>
        )}

        {(!areaOfFraud || !accountType) && (
          <div className="md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-8">
              <div className="md:col-span-4 md:col-start-3">
                <div className="flex flex-col items-center gap-4 px-8 py-6 border border-dashed rounded-md">
                  <div className="border shadow-sm rounded-md p-3"><ListTodo width="24" height="24" /></div>
                  <div className="text-lg font-bold text-center">Please select the Type</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <StepNavigation onNext={handleLegacyNext} onPrevious={onPrevious} />
    </>
    
  );
};

export default PartyInvolvedStepLegacy;
